import type { Account, GoalSpending, SavingGoal } from "../types";
import { roundToCents } from "./money-math";
import { accountAllocationSummary, goalContributionMonth, goalCurrentSaved } from "./goals";
import { accountGoalBreakdown } from "./goal-reserves";

export function isGoalFundingAccount(account: Account, currency: string): boolean {
  return account.active !== false && account.currency === currency && !/credit|card/i.test(account.type);
}

/** Unallocated cash, sharing the account screen's reserve and maximum-buffer rules. */
export function goalFundingAvailability(
  account: Account,
  goals: SavingGoal[],
  accounts: Account[],
  month: string,
  transactions: GoalSpending[],
) {
  const items = accountGoalBreakdown(goals, account.id, month, transactions, accounts);
  return accountAllocationSummary(
    account.balance,
    items.reduce((sum, item) => sum + item.reserved, 0),
    items.reduce((max, item) => Math.max(max, item.cushionAmount), 0),
  );
}

/** Both the cash ceiling and the goal's remaining target apply, in integer cents. */
export function goalTopUpLimit(goal: SavingGoal, available: number, month: string) {
  if (goal.completedDate) return { remainingAmount: 0, maximumAmount: 0 };
  // Explicit future funding already commits part of the target. Future releases
  // do not make room for another top-up before they take effect.
  const futureFundingMinor = goal.contributions
    .filter(c => !c.deletedAt && (c.kind === "ad_hoc" || c.kind === "scheduled") && goalContributionMonth(c) > month)
    .reduce((sum, c) => sum + roundToCents(c.amount), 0);
  const savedMinor = roundToCents(goalCurrentSaved(goal, month)) + futureFundingMinor;
  const remainingMinor = Math.max(0, roundToCents(goal.targetAmount) - savedMinor);
  const availableMinor = Math.max(0, roundToCents(available));
  return { remainingAmount: remainingMinor / 100, maximumAmount: Math.min(availableMinor, remainingMinor) / 100 };
}
